// src/lib/queries.ts — TanStack Query hooks over the thin API client.

import {
  QueryClient,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "./api";
import type { BodyMetric } from "./types";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

export const keys = {
  me: ["me"] as const,
  recommendations: ["recommendations"] as const,
  bodyMetrics: ["body-metrics"] as const,
  programs: ["programs"] as const,
  goals: ["goals"] as const,
  peptides: ["peptides"] as const,
  adminParticipants: (sourceApp?: string) => ["admin", "participants", sourceApp ?? null] as const,
  adminAuditLog: (action?: string) => ["admin", "audit-log", action ?? null] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: api.me });

export const useRecommendations = () =>
  useQuery({
    queryKey: keys.recommendations,
    // A user with no recommendations yet 404s — treat as "none", not an error.
    queryFn: () => api.recommendations().catch(() => null),
  });

export const useBodyMetrics = () =>
  useQuery({ queryKey: keys.bodyMetrics, queryFn: () => api.bodyMetrics() });

export const usePrograms = () =>
  useQuery({ queryKey: keys.programs, queryFn: api.programs });

export const useGoals = () =>
  useQuery({ queryKey: keys.goals, queryFn: api.goals });

export const usePeptides = () =>
  useQuery({ queryKey: keys.peptides, queryFn: api.peptides });

export const useChatStatus = () =>
  useQuery({
    queryKey: ["chat-status"] as const,
    queryFn: () => api.chatStatus().catch(() => null),
  });

export function useCreateBodyMetric() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<BodyMetric> & { date: string }) =>
      api.createBodyMetric(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.bodyMetrics }),
  });
}

export function useLogPeptideDose() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      peptide: string;
      doseMg: number;
      takenAt: string;
      planId?: number;
      site?: string;
      notes?: string;
    }) => api.createPeptideDose(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.peptides }),
  });
}

export const useAdminParticipants = (sourceApp?: string) =>
  useQuery({
    queryKey: keys.adminParticipants(sourceApp),
    queryFn: () => api.adminParticipants({ sourceApp, limit: 50 }),
  });

export const useAdminAuditLog = (action?: string) =>
  useQuery({
    queryKey: keys.adminAuditLog(action),
    queryFn: () => api.adminAuditLog({ action, limit: 50 }),
  });

export function useCreateOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      targetType: "trust_profile" | "guardian_link" | "participant";
      targetId: number;
      field: string;
      reason: string;
    }) => api.adminCreateOverride(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
}
